import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiCargoIdsRequestPost } from '@/entities/cargo-id';
import { cargoIdRequestSchema, type CargoIdRequestValues } from '@/features/cargo-id/modules/types';
import { cn } from '@/shared/utils/cn';
import type { CargoIdEntityType } from '@/shared/types/api/cargo-ids';

const BACK_TO = '/ids';

const TYPE_OPTIONS: readonly CargoIdEntityType[] = ['PHYSICAL', 'LEGAL'] as const;

export const WidgetsIdsRequestMain = () => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const toast = useToast();
  const form = useForm<CargoIdRequestValues>({
    resolver: zodResolver(cargoIdRequestSchema),
    defaultValues: {
      full_name: '',
      phone_number: '',
      // Jismoniy shaxs is the common case; the user can switch to Yuridik
      // if they're requesting an ID for a legal entity (no spending limit).
      type: 'PHYSICAL',
    },
    mode: 'onBlur',
  });

  const request = useMutate({
    api: (values: CargoIdRequestValues) => apiCargoIdsRequestPost(values),
    invalidateKeys: [queryKeys.cargoIds.all],
    onSuccess: (data) => {
      // The Cargo ID is issued immediately (new code, or an existing legacy
      // row bound by phone) — show the code; /ids displays it right away.
      toast.success(t('toast.issued', { code: data.code ?? '' }));
      navigate(BACK_TO);
    },
    onError: (_e, message) => toast.error(message),
  });

  const err = (key: keyof CargoIdRequestValues): string | undefined => {
    const message = form.formState.errors[key]?.message;
    return message ? t(message) : undefined;
  };

  return (
    <>
      <PageHeader title={t('request.title')} showBack backTo={BACK_TO} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        <form
          onSubmit={form.handleSubmit((values) => request.mutate(values))}
          className="flex flex-col gap-3"
          data-cy="cargo-id-request-form"
        >
          <Controller
            control={form.control}
            name="type"
            render={({ field }) => (
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">
                  {t('request.typeLabel')}
                </span>
                <div
                  role="radiogroup"
                  aria-label={t('request.typeLabel')}
                  className="inline-flex w-full rounded-lg border border-border bg-surface p-1"
                  data-cy="cargo-id-type-segmented"
                >
                  {TYPE_OPTIONS.map((option) => {
                    const active = field.value === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => field.onChange(option)}
                        className={cn(
                          'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          active
                            ? 'bg-brand-600 text-white shadow-sm'
                            : 'text-ink-soft hover:bg-surface-muted',
                        )}
                        data-cy={`cargo-id-type-${option}`}
                      >
                        {t(`entityType.${option}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          />
          <Input
            label={t('fields.fullName')}
            required
            error={err('full_name')}
            {...form.register('full_name')}
          />
          <Input
            label={t('fields.phone')}
            required
            inputMode="tel"
            placeholder={t('fields.phonePlaceholder')}
            error={err('phone_number')}
            {...form.register('phone_number')}
          />
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(BACK_TO)}
              disabled={request.isPending}
              block
            >
              {t('common:cancel')}
            </Button>
            <Button type="submit" loading={request.isPending} block>
              {t('request.submit')}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
